import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { apiClient } from '@/lib/api';
import GlassCard from '@/components/GlassCard';
import GlassButton from '@/components/GlassButton';

interface NodeStatus {
  id: string;
  name: string;
  status: 'ONLINE' | 'STANDBY' | 'ERROR';
  latency: string;
  load: string;
}

export default function APITestPage() {
  const [isSyncing, setIsSyncing] = useState(false);
  const [healthData, setHealthData] = useState<any>(null);
  const [nodes, setNodes] = useState<NodeStatus[]>([
    { id: '1', name: 'Neural Core v7.2', status: 'ONLINE', latency: '4ms', load: '12%' },
    { id: '2', name: 'Vector Database', status: 'ONLINE', latency: '2ms', load: '8%' },
    { id: '3', name: 'Voice Synthesis', status: 'STANDBY', latency: '12ms', load: '0%' },
    { id: '4', name: 'Market Webhook', status: 'ONLINE', latency: '45ms', load: '14%' },
  ]);

  const runHealthCheck = async () => {
    setIsSyncing(true);
    try {
      const response = await apiClient.getHealth();
      setHealthData(response.data);
      // Simulate node refresh
      setTimeout(() => {
        setNodes(prev => prev.map(node => ({
          ...node,
          latency: `${Math.floor(Math.random() * 50) + 1}ms`,
          load: `${Math.floor(Math.random() * 20) + 5}%`
        })));
        setIsSyncing(false);
      }, 1000);
    } catch (err) {
      console.error('Health check failed', err);
      setIsSyncing(false);
    }
  };

  useEffect(() => {
    runHealthCheck();
  }, []);

  return (
    <div className="min-h-screen bg-bg-primary text-text-primary selection:bg-brand-teal/30 pb-12">
      {/* Header */}
      <header className="bg-bg-secondary/50 backdrop-blur-xl border-b border-border-primary sticky top-20 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <h1 className="text-xl tracking-tight font-semibold flex items-center gap-3">
              <div className="w-2 h-2 rounded-full bg-brand-teal animate-pulse shadow-glow" />
              Sovereign Infrastructure
            </h1>
            <GlassButton 
              variant={isSyncing ? 'ghost' : 'primary'} 
              size="sm" 
              onClick={runHealthCheck} 
              disabled={isSyncing}
            >
              {isSyncing ? 'Requesting Sync...' : 'Sync Infrastructure'}
            </GlassButton>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        {/* Status Hero */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <GlassCard variant="glow" className="p-8 border-brand-teal/20">
            <div className="text-[10px] font-black uppercase tracking-[0.2em] text-text-tertiary mb-4">Network Integrity</div>
            <div className="text-4xl font-black text-text-primary mb-2 tracking-tighter">99.98%</div>
            <div className="flex items-center gap-2 text-brand-teal-light text-xs font-bold uppercase tracking-widest">
              OPTIMAL <div className="w-1 h-1 rounded-full bg-current" /> NOMINAL
            </div>
          </GlassCard>

          <GlassCard variant="subtle" className="p-8">
            <div className="text-[10px] font-black uppercase tracking-[0.2em] text-text-tertiary mb-4">Total Request Volume</div>
            <div className="text-4xl font-black text-text-primary mb-2 tracking-tighter">2.4M</div>
            <div className="text-text-secondary text-xs font-medium uppercase tracking-widest">PAST 24 HOURS</div>
          </GlassCard>

          <GlassCard variant="subtle" className="p-8">
            <div className="text-[10px] font-black uppercase tracking-[0.2em] text-text-tertiary mb-4">Global Latency</div>
            <div className="text-4xl font-black text-brand-teal-light mb-2 tracking-tighter">18ms</div>
            <div className="text-text-secondary text-xs font-medium uppercase tracking-widest">AVERAGE RESPONSE</div>
          </GlassCard>
        </div>

        {/* Node Matrix */}
        <GlassCard variant="elevated" className="p-0 overflow-hidden">
          <div className="p-8 border-b border-white/5 bg-white/[0.02]">
            <h3 className="text-sm font-black uppercase tracking-[0.2em] text-text-tertiary">Active Node Matrix</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 divide-y md:divide-y-0 md:divide-x divide-white/5">
            {nodes.map((node) => (
              <div key={node.id} className="p-8 hover:bg-white/[0.01] transition-all">
                <div className="flex justify-between items-start mb-6">
                  <div className={`w-2 h-2 rounded-full ${node.status === 'ONLINE' ? 'bg-brand-teal shadow-glow' : 'bg-text-tertiary/30'}`} />
                  <span className="text-[10px] font-black uppercase tracking-widest text-text-tertiary">{node.status}</span>
                </div>
                <h4 className="text-lg font-bold text-text-primary mb-1 tracking-tight">{node.name}</h4>
                <div className="space-y-2 pt-4">
                  <div className="flex justify-between text-[10px] font-black uppercase tracking-widest">
                    <span className="text-text-tertiary">Latency</span>
                    <span className="text-brand-teal-light">{node.latency}</span>
                  </div>
                  <div className="flex justify-between text-[10px] font-black uppercase tracking-widest">
                    <span className="text-text-tertiary">Resource Load</span>
                    <span className="text-text-primary">{node.load}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </GlassCard>

        {/* Trace Data */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <GlassCard className="p-8">
            <h3 className="text-sm font-black uppercase tracking-[0.2em] text-text-tertiary mb-6">Protocol Handshake Log</h3>
            <div className="space-y-4 font-mono text-[10px]">
              {[1,2,3,4,5].map(i => (
                <div key={i} className="flex gap-4 p-3 bg-bg-primary/50 border border-white/5 rounded-xl text-text-secondary">
                  <span className="text-brand-teal-light font-bold">[{new Date().toLocaleTimeString()}]</span>
                  <span>GET /api/v1/health/check_latency ... 200 OK</span>
                </div>
              ))}
            </div>
          </GlassCard>

          <GlassCard variant="glow" className="p-8 border-brand-plum/20">
            <h3 className="text-sm font-black uppercase tracking-[0.2em] text-text-tertiary mb-6 font-bold">Neural Engine Versioning</h3>
            <div className="space-y-6">
              <div>
                <div className="flex justify-between text-xs font-bold mb-2">
                  <span className="text-text-primary">GURU-7 CORE (LATEST)</span>
                  <span className="text-brand-plum-light">v7.2.4-stable</span>
                </div>
                <div className="w-full bg-bg-secondary h-1 rounded-full overflow-hidden">
                  <div className="bg-brand-plum-light h-full w-full shadow-glow" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-bg-secondary/30 rounded-2xl border border-white/5">
                  <div className="text-[9px] font-black text-text-tertiary uppercase tracking-widest mb-1">Architecture</div>
                  <div className="text-xs font-bold text-text-primary">Recurrent Vectorized</div>
                </div>
                <div className="p-4 bg-bg-secondary/30 rounded-2xl border border-white/5">
                  <div className="text-[9px] font-black text-text-tertiary uppercase tracking-widest mb-1">Deployment</div>
                  <div className="text-xs font-bold text-text-primary">Edge Optimized</div>
                </div>
              </div>
            </div>
          </GlassCard>
        </div>
      </div>
    </div>
  );
}
